import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { MessageCircle, Send, Bot, User, Loader2, Sparkles, HelpCircle } from 'lucide-react';

const ConversationalAssistant = ({ calculationId }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  // Function to clean markdown formatting from AI responses
  const cleanMarkdownFormatting = (text) => {
    if (typeof text !== 'string') return text;
    
    return text
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove ** bold formatting
      .replace(/\*(.*?)\*/g, '$1')     // Remove * italic formatting  
      .replace(/`(.*?)`/g, '$1')       // Remove ` code formatting
      .replace(/#{1,6}\s/g, '')        // Remove # heading formatting
      .trim();
  };

  const API = (process.env.REACT_APP_BACKEND_URL || "") || 'http://localhost:8001';

  useEffect(() => {
    initializeChat();
  }, [calculationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const initializeChat = () => {
    const newSessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    
    const welcomeMessage = calculationId 
      ? `Hi! I'm your AI assistant and I can see your trial balance data! I can help you with:

• Analyzing your specific accounts and mappings
• Creating rules based on your actual data
• Explaining why accounts are classified as they are
• Suggesting improvements to your current mappings
• Understanding DCAA compliance for your situation

Since I have access to your data, I can provide specific, actionable recommendations. What would you like to know about your trial balance?`
      : `Hi! I'm your AI assistant for DCAA compliance and account mapping. I can help you with:

• General DCAA compliance guidance
• Creating mapping rules (generic)
• Understanding allocation principles
• Best practices for government contracting

To get specific help with your data, please upload a trial balance first. What would you like help with today?`;

    setMessages([
      {
        id: 1,
        type: 'bot',
        content: cleanMarkdownFormatting(welcomeMessage),
        timestamp: new Date().toISOString(),
        dataConnected: !!calculationId
      }
    ]);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    setError(null);

    try {
      // Use the new data-connected chat endpoint
      const response = await fetch(`${API}/api/ai/chat-with-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage.content,
          calculation_id: calculationId || '',
          session_id: sessionId,
          context: { type: 'dcaa_compliance' },
          history: messages.slice(-5).map(msg => ({
            role: msg.type === 'user' ? 'user' : 'assistant',
            content: msg.content
          }))
        })
      });

      const result = await response.json();

      if (result.status === 'success') {
        // Extract content from the new response structure
        let content = result.response;
        
        // Handle the new response structure
        if (typeof result.response === 'object' && result.response.content) {
          content = result.response.content;
        }
        
        // Clean markdown formatting from the content
        const cleanedContent = cleanMarkdownFormatting(content);
        
        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: cleanedContent,
          timestamp: new Date().toISOString(),
          suggestions: result.suggestions || (result.response?.suggestions),
          actions: (result.response?.actions) || [],
          dataConnected: result.data_connected || false,
          accountsAvailable: result.accounts_available || 0
        };

        setMessages(prev => [...prev, botMessage]);
      } else {
        throw new Error(result.message || 'Failed to get response from AI assistant');
      }

    } catch (err) {
      console.error('Error sending message:', err);
      setError(`Failed to get AI response: ${err.message}`);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: "I'm sorry, I'm having trouble responding right now. Please try again in a moment.",
        timestamp: new Date().toISOString(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const executeAction = async (action) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/ai/execute-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: action,
          session_id: sessionId,
          calculation_id: calculationId
        })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        const resultMessage = {
          id: Date.now(),
          type: 'bot',
          content: `✅ Action completed: ${result.message}`,
          timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, resultMessage]);
      } else {
        throw new Error(result.message || 'Action failed');
      }

    } catch (err) {
      console.error('Error executing action:', err);
      const errorMessage = {
        id: Date.now(),
        type: 'bot',
        content: `❌ Action failed: ${err.message}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic quick questions based on data connection
  const getQuickQuestions = () => {
    if (calculationId) {
      // Data-connected questions
      return [
        "Show me my overhead accounts",
        "What accounts are unmapped?", 
        "Analyze my current rates",
        "Find potential mapping issues",
        "Create a rule from my data"
      ];
    } else {
      // General questions
      return [
        "How do I map facilities accounts?",
        "What's the difference between G&A and Overhead?",
        "Help me understand intermediate pools",
        "Show me DCAA compliance requirements",
        "Explain allocation principles"
      ];
    }
  };

  const quickQuestions = getQuickQuestions();

  const askQuickQuestion = (question) => {
    setInputMessage(question);
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-4">
      <Card className="h-[700px] flex flex-col shadow-lg border border-gray-200">
        <CardHeader className="pb-3 border-b bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-semibold">AI Assistant Chat</h3>
              <Badge className="bg-green-100 text-green-700 text-xs">
                GPT-4o Powered
              </Badge>
            </div>
            
            {/* Connection Status Indicator */}
            <div className="flex items-center gap-2">
              {calculationId ? (
                <Badge className="bg-blue-100 text-blue-700 text-xs flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  Connected to Data
                </Badge>
              ) : (
                <Badge className="bg-yellow-100 text-yellow-700 text-xs flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  No Data Connection
                </Badge>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-600">
            {calculationId 
              ? "AI can see your actual trial balance data and provide specific recommendations"
              : "Upload a trial balance to connect AI to your data for specific analysis"
            }
          </p>
        </CardHeader>
        
        <CardContent className="flex-1 flex flex-col p-0 min-h-0">
          {/* Quick Start Questions */}
          <div className="px-4 py-2 border-b bg-gray-50 flex-shrink-0">
            <div className="text-xs text-gray-600 mb-2">Quick start questions:</div>
            <div className="flex flex-wrap gap-2">
              {quickQuestions.map((question, index) => (
                <Button
                  key={index}
                  size="sm"
                  variant="ghost"
                  onClick={() => askQuickQuestion(question)}
                  className="text-xs h-6 px-2 py-1 border border-gray-200 hover:bg-blue-50"
                >
                  {question}
                </Button>
              ))}
            </div>
          </div>

          {/* Messages Area - Much larger now */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0" style={{minHeight: '400px'}}>
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex gap-2 max-w-[75%] ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  {/* Avatar */}
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.type === 'user' 
                      ? 'bg-blue-500 text-white' 
                      : message.isError 
                        ? 'bg-red-100 text-red-600' 
                        : 'bg-blue-100 text-blue-600'
                  }`}>
                    {message.type === 'user' ? (
                      <User className="w-3 h-3" />
                    ) : (
                      <Bot className="w-3 h-3" />
                    )}
                  </div>
                  
                  {/* Message Content - Fixed to prevent overflow */}
                  <div className={`px-3 py-2 rounded-lg word-wrap break-words ${
                    message.type === 'user' 
                      ? 'bg-blue-500 text-white' 
                      : message.isError 
                        ? 'bg-red-50 border border-red-200 text-red-800'
                        : 'bg-gray-100 text-gray-900'
                  }`}>
                    {/* Message Content - Fix React rendering issue */}
                    <div className="whitespace-pre-wrap text-sm break-words">
                      {typeof message.content === 'string' ? message.content : 
                       typeof message.content === 'object' ? JSON.stringify(message.content) :
                       'Invalid message format'}
                    </div>
                    
                    {/* Message timestamp */}
                    <div className={`text-xs mt-1 opacity-70`}>
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </div>
                    
                    {/* Action Suggestions */}
                    {message.actions && message.actions.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {message.actions.map((action, index) => (
                          <Button
                            key={index}
                            size="sm"
                            variant="outline"
                            onClick={() => executeAction(action)}
                            className="text-xs mr-2 mb-1"
                          >
                            {action.label}
                          </Button>
                        ))}
                      </div>
                    )}
                    
                    {/* Follow-up Suggestions */}
                    {message.suggestions && message.suggestions.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs opacity-70 mb-1">Suggestions:</div>
                        <div className="flex flex-wrap">
                          {message.suggestions.map((suggestion, index) => (
                            <Button
                              key={index}
                              size="sm"
                              variant="ghost"
                              onClick={() => askQuickQuestion(suggestion)}
                              className="text-xs mr-2 mb-1"
                            >
                              {suggestion}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-start">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="bg-gray-100 px-4 py-2 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm text-gray-600">AI is thinking...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Error Alert */}
          {error && (
            <div className="px-4 py-2">
              <Alert variant="destructive">
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            </div>
          )}

          {/* Quick Questions */}
          {messages.length <= 1 && (
            <div className="px-4 py-2 border-t bg-gray-50">
              <div className="text-xs text-gray-600 mb-2">Quick questions to get started:</div>
              <div className="flex flex-wrap gap-1">
                {quickQuestions.map((question, index) => (
                  <Button
                    key={index}
                    size="sm"
                    variant="outline"
                    onClick={() => askQuickQuestion(question)}
                    className="text-xs"
                  >
                    <HelpCircle className="w-3 h-3 mr-1" />
                    {question}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area - Compact */}
          <div className="px-4 py-2 border-t bg-white flex-shrink-0">
            <div className="flex gap-2">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about account mapping or DCAA compliance..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                rows="2"
                disabled={loading}
                style={{minHeight: '42px', maxHeight: '84px'}}
              />
              <Button 
                onClick={sendMessage}
                disabled={!inputMessage.trim() || loading}
                className="px-3 py-2 flex-shrink-0"
                size="sm"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            <div className="text-xs text-gray-500 mt-1">
              Press Enter to send, Shift+Enter for new line
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Simple Badge component if not already imported
const Badge = ({ children, className = '', variant = 'default' }) => {
  const variants = {
    default: 'bg-blue-100 text-blue-700',
    outline: 'border border-gray-300 text-gray-700'
  };
  
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

export default ConversationalAssistant;
